import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row, Table } from 'react-bootstrap';
import ConfirmModal from '@/components/common/ConfirmModal';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
    INSTITUTION_KEYS,
    institutionLabel,
} from '@/shared/constants/institutions';
import { useAuth } from '@/shared/contexts/AuthContext';
import adminService from '@/shared/services/adminService';
import { serverMessage } from '@/shared/utils/serverMessage';

const EMPTY_FILTERS = { q: '', institution: '', role: '' };

// The route's own default is 50, which truncates a campus directory silently
// while the heading prints the true total. A directory is read by scanning
// rather than by paging, so the tab asks for one page large enough to hold
// it; past that the notice under the table is what keeps the count honest.
const PAGE_LIMIT = 100;

// Mirrors User.ROLES on the server. A role stored there and missing here
// renders as a raw enum value in a badge.
const ROLE_LABELS = Object.freeze({
    member: 'Member',
    coordinator: 'Coordinator',
    admin: 'Admin',
});

// An empty box means "no filter", and the server rejects an empty string for
// institution and role, so blanks are dropped rather than sent.
function toParams(filters) {
    const params = { limit: PAGE_LIMIT };
    for (const [key, value] of Object.entries(filters)) {
        if (value) {
            params[key] = value;
        }
    }
    return params;
}

const hasRole = (user, role) => user.roles.includes(role);
const isAdmin = (user) => hasRole(user, 'admin');

// Only an admin reaches this tab, so the caller's own row always carries the
// role and the control on it is always the removal one. The server refuses
// that removal with a 400; this sentence is what keeps the refusal from being
// the first place a coordinator reads the rule.
const SELF_DEMOTION_TITLE =
    'You cannot remove your own admin role. Ask another admin.';

// Deactivating your own account locks you out of the whole admin area, since
// the next request from it is refused before any admin route runs. The server
// refuses it with a 400; this sentence is what keeps the refusal from being
// the first place a coordinator reads the rule. Reactivate carries no such
// title: an admin reading this table is signed in, so their own row is never
// the inactive one.
const SELF_DEACTIVATE_TITLE =
    'You cannot deactivate your own account. Ask another admin.';

/**
 * The roles array that grants or revokes `role`.
 *
 * The route replaces the array rather than patching it and refuses an empty
 * one, so a profile left with no role falls back to member instead of to
 * nothing.
 */
function nextRoles(user, role) {
    if (!hasRole(user, role)) {
        return [...user.roles, role];
    }
    const remaining = user.roles.filter((held) => held !== role);
    return remaining.length > 0 ? remaining : ['member'];
}

/**
 * What the confirmation says for each role change.
 *
 * A coordinator's reach is their own institution, which is never editable, so
 * the confirmation names it: granting the role to someone at the wrong campus
 * is the mistake worth stopping here.
 */
function roleChangeCopy({ user, role }) {
    const removing = hasRole(user, role);
    if (role === 'coordinator') {
        const campus = institutionLabel(user.institution);
        return {
            removing,
            title: removing
                ? 'Remove this coordinator role?'
                : `Make this member a ${campus} coordinator?`,
            confirm: removing ? 'Remove coordinator' : 'Make coordinator',
            body: removing
                ? `${user.name} keeps their account and loses the ${campus}` +
                  ' coordinator area.'
                : `${user.name} gets the coordinator area for ${campus}:` +
                  " that institution's facilities contacts and policy text," +
                  ' its guidelines section, moderation of its listings, and' +
                  ' the reports.',
        };
    }
    return {
        removing,
        title: removing
            ? 'Remove this admin role?'
            : 'Make this member an admin?',
        confirm: removing ? 'Remove admin' : 'Make admin',
        body: removing
            ? `${user.name} keeps their account and loses the admin area.`
            : `${user.name} gets the admin area: the member directory, the` +
              ' guidelines, the facilities addresses, and the reports.',
    };
}

function UsersTab() {
    const { userProfile } = useAuth();

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    // Applied separately from the form state so typing in the search box does
    // not fire a request per keystroke; only a submit moves this.
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionError, setActionError] = useState('');
    const [pendingRole, setPendingRole] = useState(null);
    const [pendingDeactivate, setPendingDeactivate] = useState(null);

    const load = useCallback(async (active) => {
        setLoading(true);
        setError('');
        try {
            const data = await adminService.listUsers(toParams(active));
            setUsers(data.users || []);
            setTotal(data.total || 0);
        } catch (_error) {
            setUsers([]);
            setTotal(0);
            setError(
                'Could not load the member directory. Check your connection' +
                    ' and try again.'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(applied);
    }, [applied, load]);

    const replace = (updated) =>
        setUsers((previous) =>
            previous.map((user) =>
                user._id === updated._id ? { ...user, ...updated } : user
            )
        );

    const runAction = async (act, fallback) => {
        setActionError('');
        try {
            replace(await act());
        } catch (caught) {
            setActionError(serverMessage(caught, fallback));
        }
    };

    const handleRoleChange = () => {
        const { user: target, role } = pendingRole;
        setPendingRole(null);
        return runAction(
            () =>
                adminService.setUserRoles(target._id, nextRoles(target, role)),
            'Could not change that role. Try again.'
        );
    };

    const handleDeactivate = () => {
        const target = pendingDeactivate;
        setPendingDeactivate(null);
        return runAction(
            () => adminService.setUserActive(target._id, false),
            'Could not deactivate that member. Try again.'
        );
    };

    const handleReactivate = (user) =>
        runAction(
            () => adminService.setUserActive(user._id, true),
            'Could not reactivate that member. Try again.'
        );

    const set = (key) => (event) =>
        setFilters({ ...filters, [key]: event.target.value });

    const handleSearch = (event) => {
        event.preventDefault();
        setApplied(filters);
    };

    const roleCopy = pendingRole ? roleChangeCopy(pendingRole) : null;

    return (
        <>
            <h2 className="h4 mb-3">
                Members{' '}
                <span className="text-muted fs-6">
                    {total} {total === 1 ? 'member' : 'members'}
                </span>
            </h2>
            <Form onSubmit={handleSearch} className="mb-3">
                <Row className="g-2 align-items-end">
                    <Col md={4}>
                        <Form.Group controlId="admin-users-q">
                            <Form.Label>Search members</Form.Label>
                            <Form.Control
                                type="search"
                                value={filters.q}
                                onChange={set('q')}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="admin-users-institution">
                            <Form.Label>Institution</Form.Label>
                            <Form.Select
                                value={filters.institution}
                                onChange={set('institution')}
                            >
                                <option value="">All institutions</option>
                                {INSTITUTION_KEYS.map((key) => (
                                    <option key={key} value={key}>
                                        {institutionLabel(key)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="admin-users-role">
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                                value={filters.role}
                                onChange={set('role')}
                            >
                                <option value="">All roles</option>
                                {Object.entries(ROLE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Button type="submit" className="w-100">
                            Search
                        </Button>
                    </Col>
                </Row>
            </Form>
            {error && <Alert variant="danger">{error}</Alert>}
            {actionError && <Alert variant="danger">{actionError}</Alert>}
            {loading && <LoadingSpinner message="Loading members" />}
            {!loading && !error && users.length === 0 && (
                <p className="text-muted">
                    No member matches those filters. Try clearing one.
                </p>
            )}
            {!loading && !error && users.length > 0 && (
                <>
                    <Table hover responsive className="align-middle">
                        <thead>
                            <tr>
                                <th scope="col">Member</th>
                                <th scope="col">Campus</th>
                                <th scope="col">Roles</th>
                                <th scope="col">Status</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => {
                                const self = user._id === userProfile?._id;
                                return (
                                    <tr key={user._id}>
                                        {/* Name, address, and department in
                                            one cell: they identify one
                                            person, and as three columns they
                                            pushed the actions off a laptop
                                            screen. */}
                                        <td>
                                            <div className="fw-semibold afe-nowrap">
                                                {user.name}
                                            </div>
                                            <div className="small afe-nowrap">
                                                {user.email}
                                            </div>
                                            {user.department && (
                                                <div className="small text-muted">
                                                    {user.department}
                                                </div>
                                            )}
                                        </td>
                                        <td className="afe-nowrap">
                                            <InstitutionLogo
                                                institution={user.institution}
                                            />
                                        </td>
                                        <td>
                                            {user.roles.map((role) => (
                                                <Badge
                                                    key={role}
                                                    bg={
                                                        role === 'admin'
                                                            ? 'primary'
                                                            : 'secondary'
                                                    }
                                                    className="afe-table-badge me-1"
                                                >
                                                    {ROLE_LABELS[role] || role}
                                                </Badge>
                                            ))}
                                        </td>
                                        <td>
                                            <Badge
                                                bg={
                                                    user.isActive
                                                        ? 'success'
                                                        : 'dark'
                                                }
                                                className="afe-table-badge"
                                            >
                                                {user.isActive
                                                    ? 'Active'
                                                    : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="afe-actions flex-nowrap">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="afe-btn-admin"
                                                    disabled={self}
                                                    title={
                                                        self
                                                            ? SELF_DEMOTION_TITLE
                                                            : undefined
                                                    }
                                                    aria-label={
                                                        isAdmin(user)
                                                            ? `Remove admin from ${user.name}`
                                                            : `Make admin: ${user.name}`
                                                    }
                                                    onClick={() =>
                                                        setPendingRole({
                                                            user,
                                                            role: 'admin',
                                                        })
                                                    }
                                                >
                                                    {isAdmin(user)
                                                        ? 'Remove admin'
                                                        : 'Make admin'}
                                                </Button>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="afe-btn-coord"
                                                    aria-label={
                                                        hasRole(
                                                            user,
                                                            'coordinator'
                                                        )
                                                            ? `Remove coordinator from ${user.name}`
                                                            : `Make coordinator: ${user.name}, ${institutionLabel(user.institution)}`
                                                    }
                                                    onClick={() =>
                                                        setPendingRole({
                                                            user,
                                                            role: 'coordinator',
                                                        })
                                                    }
                                                >
                                                    {hasRole(
                                                        user,
                                                        'coordinator'
                                                    )
                                                        ? 'Remove coordinator'
                                                        : 'Make coordinator'}
                                                </Button>
                                                {user.isActive ? (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="afe-btn-active"
                                                        disabled={self}
                                                        title={
                                                            self
                                                                ? SELF_DEACTIVATE_TITLE
                                                                : undefined
                                                        }
                                                        aria-label={`Deactivate ${user.name}`}
                                                        onClick={() =>
                                                            setPendingDeactivate(
                                                                user
                                                            )
                                                        }
                                                    >
                                                        Deactivate
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="afe-btn-active"
                                                        aria-label={`Reactivate ${user.name}`}
                                                        onClick={() =>
                                                            handleReactivate(
                                                                user
                                                            )
                                                        }
                                                    >
                                                        Reactivate
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                    {users.length < total && (
                        <p className="text-muted small">
                            Showing {users.length} of {total} members. Narrow
                            the search to reach the rest.
                        </p>
                    )}
                </>
            )}
            <ConfirmModal
                show={Boolean(pendingRole)}
                title={roleCopy?.title || ''}
                confirmLabel={roleCopy?.confirm || 'Confirm'}
                cancelLabel="Cancel"
                variant={roleCopy?.removing ? 'danger' : 'primary'}
                onCancel={() => setPendingRole(null)}
                onConfirm={handleRoleChange}
            >
                <p className="mb-0">{roleCopy?.body}</p>
            </ConfirmModal>
            <ConfirmModal
                show={Boolean(pendingDeactivate)}
                title="Deactivate this member?"
                confirmLabel="Deactivate"
                cancelLabel="Keep active"
                onCancel={() => setPendingDeactivate(null)}
                onConfirm={handleDeactivate}
            >
                <p className="mb-0">
                    {pendingDeactivate?.name} is marked inactive in the
                    directory. Their listings and requests stay where they are,
                    and you can reactivate them here.
                </p>
            </ConfirmModal>
        </>
    );
}

export default UsersTab;
